// 3D プレビューの単一インスタンスを管理する。
// WebGL コンテキストの作り直しを避けるため、DOM 要素とビューアは 1 つだけ作り、
// タブ切り替え時は同じ要素を別の親へ付け替える。

import { AvatarViewer, type BackgroundKey } from "../render/viewer.ts";
import { DEFAULT_LIGHTING, type LightingSettings } from "../render/liltoon.ts";
import type { FaceClip, LoadedAvatar } from "../core/types.ts";
import { store } from "../store/state.ts";
import { el, storageGet, storageSet } from "./dom.ts";

let wrap: HTMLDivElement | null = null;
let canvasHost: HTMLDivElement | null = null;
let viewer: AvatarViewer | null = null;
let currentAvatarId: string | null = null;
let shapeErrorBox: HTMLDivElement | null = null;

/** プレビュー領域サイズのユーザー調整値を保存するキー */
const LS_HEIGHT_KEY = "facemaker.previewHeight"; // vh 単位の数値
const LS_SPLIT_KEY = "facemaker.previewSplitRatio"; // 0〜1（プレビュー側の比率）
const PC_BREAKPOINT = 900;
const MIN_PANE_PX = 280;

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function loadPreviewHeightVh(): number | null {
  const raw = storageGet(LS_HEIGHT_KEY);
  if (!raw) return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

function savePreviewHeightVh(vh: number): void {
  storageSet(LS_HEIGHT_KEY, String(vh));
}

function loadSplitRatio(): number | null {
  const raw = storageGet(LS_SPLIT_KEY);
  if (!raw) return null;
  const v = Number(raw);
  return Number.isFinite(v) && v > 0 && v < 1 ? v : null;
}

function saveSplitRatio(ratio: number): void {
  storageSet(LS_SPLIT_KEY, String(ratio));
}

/** タブを跨いでも維持する表示設定 */
const view = {
  background: "dark" as BackgroundKey,
  lighting: { ...DEFAULT_LIGHTING } as LightingSettings,
  settingsOpen: false,
};

const BG_LABELS: Array<{ key: BackgroundKey; label: string }> = [
  { key: "dark", label: "暗" },
  { key: "light", label: "明" },
  { key: "green", label: "緑" },
];

function lightSlider(
  label: string,
  key: keyof LightingSettings,
  min: number,
  max: number,
  step: number,
): HTMLElement {
  const out = el("span", { class: "val", text: String(view.lighting[key]) });
  const input = el("input", {
    type: "range",
    min: String(min),
    max: String(max),
    step: String(step),
    value: String(view.lighting[key]),
    "aria-label": label,
    oninput: (e: Event) => {
      const v = Number((e.target as HTMLInputElement).value);
      view.lighting[key] = v;
      out.textContent = String(v);
      viewer?.setLighting(view.lighting);
    },
  });
  return el("div", { class: "light-row" }, el("span", { class: "lbl", text: label }), input, out);
}

function settingsPanel(): HTMLElement {
  const bgRow = el("div", { class: "light-row" }, el("span", { class: "lbl", text: "背景" }));
  const btns = el("div", { class: "bg-btns" });
  for (const b of BG_LABELS) {
    const btn = el("button", {
      class: `btn${view.background === b.key ? " primary" : " ghost"}`,
      text: b.label,
      onclick: () => {
        view.background = b.key;
        viewer?.setBackground(b.key);
        for (const other of Array.from(btns.children)) {
          other.className = other === btn ? "btn primary" : "btn ghost";
        }
      },
    });
    btns.appendChild(btn);
  }
  bgRow.appendChild(btns);

  return el(
    "div",
    { class: "preview-settings", hidden: !view.settingsOpen },
    bgRow,
    lightSlider("光の向き", "yaw", -180, 180, 5),
    lightSlider("光の高さ", "pitch", -80, 80, 5),
    lightSlider("光の強さ", "intensity", 0, 1.2, 0.05),
    lightSlider("環境光", "ambient", 0.1, 1.2, 0.05),
  );
}

/** 操作パネルを最低限見せておくために確保する高さ（px） */
const MIN_PANEL_VISIBLE_PX = 100;
/** プレビューを縮められる下限（ドラッグハンドルが見える程度）（px） */
const MIN_PREVIEW_PX = 56;

/** 初回だけ「ドラッグでプレビューを縮められる」ことを伝えるヒントを見せたかどうか（モバイル/PC別キー） */
const LS_RESIZE_HINT_SEEN_KEY = "facemaker.hint.previewResizeSeen";
const LS_RESIZE_HINT_V_SEEN_KEY = "facemaker.hint.previewResizeVSeen";

/** 高さ/幅どちらのドラッグハンドルにも使う、一度きりの操作ヒント表示 */
function showResizeHintOnce(
  wrapEl: HTMLDivElement,
  handle: HTMLDivElement,
  extraClass: string,
  text: string,
  storageKey: string,
): void {
  if (storageGet(storageKey)) return;

  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    storageSet(storageKey, "1");
    hint.remove();
  };
  const closeBtn = el("button", {
    type: "button",
    class: "preview-resize-hint-close",
    "aria-label": "ヒントを閉じる",
    text: "×",
    onclick: (e: Event) => {
      e.stopPropagation();
      dismiss();
    },
  });
  const hint = el(
    "div",
    { class: `preview-resize-hint ${extraClass}` },
    el("span", { text }),
    closeBtn,
  ) as HTMLDivElement;
  hint.addEventListener("click", dismiss);
  handle.addEventListener("pointerdown", dismiss, { once: true });
  wrapEl.appendChild(hint);
  setTimeout(dismiss, 5000);
}

/** モバイルで高さドラッグハンドルを初めて見せるとき、一度だけ操作方法のヒントを表示する */
function maybeShowResizeHint(wrapEl: HTMLDivElement, handle: HTMLDivElement): void {
  if (window.innerWidth >= PC_BREAKPOINT) return;
  showResizeHintOnce(wrapEl, handle, "", "↕ ドラッグしてプレビューの大きさを変えられます", LS_RESIZE_HINT_SEEN_KEY);
}

/** PCで幅ドラッグハンドルを初めて見せるとき、一度だけ操作方法のヒントを表示する */
function maybeShowWidthResizeHint(wrapEl: HTMLDivElement, handle: HTMLDivElement): void {
  if (window.innerWidth < PC_BREAKPOINT) return;
  showResizeHintOnce(
    wrapEl,
    handle,
    "preview-resize-hint-v",
    "↔ ドラッグしてプレビューの幅を変えられます",
    LS_RESIZE_HINT_V_SEEN_KEY,
  );
}

/** 高さドラッグハンドル（モバイル、縦並びレイアウト時のみ有効） */
function heightResizeHandle(target: HTMLDivElement): HTMLDivElement {
  const handle = el(
    "div",
    { class: "preview-resize-h", "aria-label": "プレビューの高さを調整" },
    el("div", { class: "preview-resize-h-icon", "aria-hidden": "true", text: "▲▼" }),
  ) as HTMLDivElement;
  let startY = 0;
  let startH = 0;
  let maxH = 0;
  let dragging = false;
  handle.addEventListener("pointerdown", (e) => {
    if (window.innerWidth >= PC_BREAKPOINT) return;
    dragging = true;
    startY = e.clientY;
    startH = target.getBoundingClientRect().height;
    // .content はヘッダ/タブバーを除いた実際の可視スクロール領域なので、
    // ここを基準にすればヘッダ等の高さを個別に見積もる必要がない。
    const contentH = target.closest<HTMLElement>(".content")?.clientHeight ?? window.innerHeight;
    maxH = contentH - MIN_PANEL_VISIBLE_PX;
    handle.setPointerCapture(e.pointerId);
  });
  handle.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const dy = e.clientY - startY;
    const newH = clamp(startH + dy, MIN_PREVIEW_PX, Math.max(MIN_PREVIEW_PX, maxH));
    const vh = (newH / window.innerHeight) * 100;
    // sticky-bar（検索バー）は preview-wrap の兄弟要素なので、
    // 共通の親（.view-split）に変数を持たせて追従させる。
    (target.parentElement ?? target).style.setProperty("--preview-h", `${vh}vh`);
  });
  const finish = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    handle.releasePointerCapture(e.pointerId);
    const raw = (target.parentElement ?? target).style.getPropertyValue("--preview-h");
    const vh = Number.parseFloat(raw);
    if (Number.isFinite(vh)) savePreviewHeightVh(vh);
  };
  handle.addEventListener("pointerup", finish);
  handle.addEventListener("pointercancel", finish);
  return handle;
}

/** 幅ドラッグハンドル（PC、.view-split の2カラムレイアウト時のみ有効） */
function widthResizeHandle(target: HTMLDivElement): HTMLDivElement {
  const handle = el("div", { class: "preview-resize-v", "aria-hidden": "true" }) as HTMLDivElement;
  let startX = 0;
  let startWrapW = 0;
  let startTotal = 0;
  let panelEl: HTMLElement | null = null;
  let dragging = false;
  handle.addEventListener("pointerdown", (e) => {
    if (window.innerWidth < PC_BREAKPOINT) return;
    panelEl = target.parentElement?.querySelector<HTMLElement>(".view-panel") ?? null;
    if (!panelEl) return;
    dragging = true;
    startX = e.clientX;
    startWrapW = target.getBoundingClientRect().width;
    startTotal = startWrapW + panelEl.getBoundingClientRect().width;
    handle.setPointerCapture(e.pointerId);
  });
  handle.addEventListener("pointermove", (e) => {
    if (!dragging || !panelEl) return;
    const dx = e.clientX - startX;
    const maxWrapW = startTotal - MIN_PANE_PX;
    const newWrapW = clamp(startWrapW + dx, MIN_PANE_PX, maxWrapW);
    const ratio = newWrapW / startTotal;
    target.style.flex = `0 0 ${ratio * 100}%`;
    panelEl.style.flex = `1 1 ${(1 - ratio) * 100}%`;
  });
  const finish = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    handle.releasePointerCapture(e.pointerId);
    const m = /^0 0 ([\d.]+)%$/.exec(target.style.flex);
    if (m) saveSplitRatio(Number(m[1]) / 100);
    panelEl = null;
  };
  handle.addEventListener("pointerup", finish);
  handle.addEventListener("pointercancel", finish);
  return handle;
}

/** localStorage に保存済みのサイズ調整値を復元する */
function restoreSavedSize(target: HTMLDivElement): void {
  const vh = loadPreviewHeightVh();
  if (vh !== null) (target.parentElement ?? target).style.setProperty("--preview-h", `${vh}vh`);
  // .view-panel は attachPreview 呼び出し元がこの直後に追加するため、
  // 同期処理完了後の rAF で適用する。
  requestAnimationFrame(() => {
    const ratio = loadSplitRatio();
    if (ratio === null) return;
    const panelEl = target.parentElement?.querySelector<HTMLElement>(".view-panel");
    if (!panelEl) return;
    target.style.flex = `0 0 ${ratio * 100}%`;
    panelEl.style.flex = `1 1 ${(1 - ratio) * 100}%`;
  });
}

function ensureDom(): HTMLDivElement {
  if (wrap) return wrap;
  canvasHost = el("div", { style: { position: "absolute", inset: "0" } });
  shapeErrorBox = el("div", { class: "preview-shape-error", hidden: true });
  const panel = settingsPanel();
  wrap = el(
    "div",
    { class: "preview-wrap" },
    canvasHost,
    shapeErrorBox,
    panel,
    el(
      "div",
      { class: "preview-overlay" },
      el("button", {
        class: "btn",
        text: "⚙",
        "aria-label": "表示設定",
        onclick: () => {
          view.settingsOpen = !view.settingsOpen;
          panel.hidden = !view.settingsOpen;
        },
      }),
      el("button", { class: "btn", text: "顔に寄る", onclick: () => viewer?.frameFace() }),
      el("button", { class: "btn", text: "全体", onclick: () => viewer?.frameAll() }),
    ),
  );
  const hHandle = heightResizeHandle(wrap);
  const vHandle = widthResizeHandle(wrap);
  wrap.appendChild(hHandle);
  wrap.appendChild(vHandle);
  maybeShowResizeHint(wrap, hHandle);
  maybeShowWidthResizeHint(wrap, vHandle);
  return wrap;
}

let pcResizeListenerInstalled = false;

/** PC版（2カラムレイアウト）のプレビュー高さを `.content` の実際の可視高さぴったりに合わせる。 */
function syncPcPreviewHeight(node: HTMLDivElement): void {
  if (window.innerWidth < PC_BREAKPOINT) return;
  const contentEl = node.closest<HTMLElement>(".content");
  const root = node.parentElement;
  if (!contentEl || !root) return;
  root.style.setProperty("--pc-preview-h", `${contentEl.clientHeight}px`);
}

/** プレビュー要素を parent の先頭に配置する。アバター未読み込みならプレースホルダを返す。 */
export function attachPreview(parent: HTMLElement): void {
  const avatar = store.avatar;
  if (!avatar) {
    parent.appendChild(
      el(
        "div",
        { class: "preview-wrap" },
        el("div", { class: "preview-empty", text: "「アバター」タブから .fmavatar を読み込むとここにプレビューが表示されます。" }),
      ),
    );
    return;
  }
  const node = ensureDom();
  parent.appendChild(node);
  restoreSavedSize(node);
  // 呼び出し元がこの直後に root を document へ挿入するため、
  // .content 祖先が見つかるよう挿入後の rAF で高さを計算する。
  requestAnimationFrame(() => syncPcPreviewHeight(node));
  if (!pcResizeListenerInstalled) {
    pcResizeListenerInstalled = true;
    window.addEventListener("resize", () => {
      if (wrap) syncPcPreviewHeight(wrap);
    });
  }

  if (currentAvatarId !== avatar.id || !viewer) {
    // WebGL 初期化はここで初めて行う（読み込み前にコンテキストを取らない）
    if (!viewer && canvasHost) {
      viewer = new AvatarViewer(canvasHost);
      viewer.setBackground(view.background);
      viewer.setLighting(view.lighting);
    }
    viewer?.setAvatar(avatar);
    currentAvatarId = avatar.id;
    // シェイプデルタテクスチャが端末の上限を超えた場合はプレビュー上に表示する
    const errs = viewer?.getShapeErrors() ?? [];
    if (shapeErrorBox) {
      shapeErrorBox.textContent = errs.join("\n");
      shapeErrorBox.hidden = errs.length === 0;
    }
  }
  refreshPreview();
}

// --- ジェスチャー画面でキーフレームクリップをプレビュー中のときのループ再生 ---
// 代表ポーズ（最初のキーフレーム）だけでは静止画になってわかりにくいため、
// requestAnimationFrame でクリップの長さぶんループさせて実際の動きを見せる。
let animHandle: number | null = null;
let animKey: string | null = null;
let animStart = 0;
/** 表情タブの手動再生（playingClipIndex 経由）で、直近どの playSession を反映済みかを覚えておく。 */
let animSession = -1;

/** 現在アニメーション再生すべきキーフレームクリップ（ジェスチャー画面でのプレビュー中、または表情タブの「▶ 再生」中） */
function animatingClip(): FaceClip | null {
  if (store.playingClipIndex !== null && store.playingClipIndex === store.editor.editingClipIndex) {
    const clip = store.clipByIndex(store.playingClipIndex);
    if (clip && clip.type === "keyframe" && clip.keyframes && clip.keyframes.length > 1) return clip;
  }
  if (store.tab !== "gesture") return null;
  const clip = store.previewingClip();
  if (clip && clip.type === "keyframe" && clip.keyframes && clip.keyframes.length > 1) return clip;
  return null;
}

function stopAnimLoop(): void {
  if (animHandle !== null) {
    cancelAnimationFrame(animHandle);
    animHandle = null;
  }
  animKey = null;
  animSession = -1;
}

function animTick(): void {
  const clip = animatingClip();
  if (!clip) {
    stopAnimLoop();
    refreshPreview();
    return;
  }
  const key = `${clip.index}:${clip.keyframes!.map((k) => k.time).join(",")}`;
  // 表情タブの手動再生（「再生」「頭から再生」ボタン）中は、押すたびに playSession が増分される。
  // 「現在位置から」「頭から」の開始時刻の違いは this.playStartTime に入っており、
  // 同じクリップのままでも session が変わったら開始時刻を読み直して巻き戻す。
  const isManualPlay = store.playingClipIndex !== null && store.playingClipIndex === clip.index;
  if (isManualPlay) {
    if (key !== animKey || store.playSession !== animSession) {
      animKey = key;
      animSession = store.playSession;
      animStart = performance.now() - store.playStartTime * 1000;
    }
  } else if (key !== animKey) {
    animKey = key;
    animStart = performance.now();
  }
  const duration = Math.max(clip.durationSeconds, 0.05);
  const elapsed = ((performance.now() - animStart) / 1000) % duration;
  viewer?.applyShapeValues(store.sampleClipAt(clip, elapsed));
  animHandle = requestAnimationFrame(animTick);
}

/** 現在の編集状態をプレビューへ反映する（再描画を伴わない軽量更新） */
export function refreshPreview(): void {
  const clip = animatingClip();
  if (clip) {
    if (animHandle === null) animHandle = requestAnimationFrame(animTick);
    return;
  }
  stopAnimLoop();
  viewer?.applyShapeValues(store.currentPreviewValues());
}

/** アバターを差し替える（次回 attach 時に反映） */
export function invalidatePreview(avatar: LoadedAvatar | null): void {
  if (!avatar) {
    currentAvatarId = null;
    viewer?.resetShapes();
    return;
  }
  if (currentAvatarId !== avatar.id) currentAvatarId = null;
}

export function disposePreview(): void {
  stopAnimLoop();
  viewer?.dispose();
  viewer = null;
  wrap = null;
  canvasHost = null;
  shapeErrorBox = null;
  currentAvatarId = null;
}
